import { Box, Button, Tooltip } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";

interface Props {
  onClick: () => void;
  /**
   * What this surface calls its history. Defaults to "History"; the field map says "Versions"
   * because that is the word its drawer, its runs and its pinning have always used.
   */
  label?: string;
  /** Tooltip. Defaults to a generic one; hosts name their own aggregate. */
  title?: string;
  /**
   * Optional count. Shown only where the list is already loaded — the field map loads its
   * versions with the page, so a number is free there. Surfaces that fetch on open must leave
   * this out: a badge reading "0" until you click is worse than no badge.
   */
  count?: number;
  disabled?: boolean;
}

/**
 * The one History trigger, used by every aggregate that has a version drawer (MPN-1336).
 *
 * There were four before this: MUI buttons on the field map and the catalog, and two different
 * native/Bootstrap buttons on the engagement header and the contract hero. That is a problem
 * beyond tidiness, because the surfaces are reachable from each other — a contract is opened from
 * inside an engagement — so the same affordance was changing shape as you navigated.
 *
 * MUI rather than Bootstrap deliberately. The drawer it opens is MUI and renders as an overlay
 * regardless of its host, so the trigger matching the drawer keeps the interaction coherent even
 * on a Bootstrap-styled page like the contract hero.
 *
 * Proportions come from the engagement header's `&__history` rule, which was the most
 * deliberately styled of the four: 11.5px/600 text, 7px radius, and the light purple border that
 * darkens on hover.
 */
export default function HistoryButton({
  onClick,
  label = "History",
  title = "Change history",
  count,
  disabled = false,
}: Props) {
  return (
    <Tooltip title={title}>
      <Button
        variant="outlined"
        onClick={onClick}
        disabled={disabled}
        startIcon={<HistoryIcon sx={{ fontSize: 15 }} />}
        sx={{
          textTransform: "none",
          fontSize: 11.5,
          fontWeight: 600,
          lineHeight: 1.4,
          px: 1.4,
          py: 0.5,
          minWidth: 0,
          borderRadius: "7px",
          whiteSpace: "nowrap",
          flexShrink: 0,
          color: "#845adf",
          borderColor: "#d9c9f5",
          bgcolor: "#fff",
          "&:hover": { bgcolor: "#faf8fe", borderColor: "#845adf" },
          // The icon's own margin is tuned for MUI's default 14px text; at 11.5 it reads loose.
          // With no label there is nothing to its right, so the margin only pushes the icon
          // off-centre — the ingest Files page uses the icon-only form because its row already
          // carries six controls.
          "& .MuiButton-startIcon": { mr: label ? 0.6 : 0, ml: 0 },
        }}
      >
        {label}
        {count != null && count > 0 && (
          <Box
            component="span"
            sx={{
              ml: 0.75,
              px: 0.75,
              borderRadius: 5,
              bgcolor: "#845adf",
              color: "#fff",
              fontSize: 10,
              fontWeight: 600,
              lineHeight: "15px",
            }}
          >
            {count}
          </Box>
        )}
      </Button>
    </Tooltip>
  );
}
